/**
 * What to do next.
 *
 * The queue itself is right: it reads every site, picks at most one thing per
 * site and at most five in all, and orders them by how much they matter. What
 * it looked like was four colours on a left border (red, blue, amber, green)
 * matched by four icon colours, on cards that lifted when you pointed at them
 * and slid in one at a time from the left.
 *
 * A queue is already in priority order, which is what the colours were for.
 * The order says it, so the tiers keep working and stop being painted.
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  Radar,
  Bot,
  Sparkles,
  TrendingUp,
  CheckCircle2,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react'
import { DsIsland } from '@/design-system/island'
import { Button, Card, Proof, Skeleton } from '@/design-system/primitives'
import { ToDo } from '@/design-system/data'
import '@/design-system/dashboard.css'
import '@/design-system/data.css'
import { getBusinessProfileStatusForDomain, type BusinessProfile } from '@/utils/businessProfileStatus'
import ScanTypeModal from './ScanTypeModal'
import type { UnifiedDomain, DomainDetailSummary, Organization } from '@shared/types'

// ─── Types ───────────────────────────────────────────────────────────────────

interface GuidedAction {
  id: string
  domainId: string
  domainName: string
  domainDisplayName: string | null
  tier: 1 | 2 | 3 | 4
  title: string
  description: string
  ctaLabel: string
  ctaRoute: string
  icon: LucideIcon
  isScanAction?: boolean
  isRescan?: boolean
}

interface GuidedActionsProps {
  domains: UnifiedDomain[]
  summaries: Map<string, DomainDetailSummary>
  organizations: Organization[]
  isPro: boolean
  isLoading: boolean
  canManageBusinessProfile?: boolean
  className?: string
}

// ─── Tier Colors ─────────────────────────────────────────────────────────────

/*
  Four tiers, once drawn as four hues on a left border. The list is in order,
  so its position already says everything the colour was saying. What is left
  is the word, which says what KIND of job it is, and that is real.
*/
const tierLabel: Record<number, string> = {
  1: 'Worth fixing',
  2: 'Not set up yet',
  3: 'Could be better',
  4: 'Nothing to do',
}

// ─── Action Generation Algorithm ─────────────────────────────────────────────

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function generateGuidedActions(
  domains: UnifiedDomain[],
  summaries: Map<string, DomainDetailSummary>,
  organizations: Organization[],
  isPro: boolean,
  canManageBusinessProfile: boolean
): GuidedAction[] {
  const actions: GuidedAction[] = []
  const businessProfiles = organizations as BusinessProfile[]

  for (const domain of domains) {
    const summary = summaries.get(domain.id)
    const slug = encodeURIComponent(domain.domain)
    const name = domain.displayName || domain.domain

    // ── TIER 1: Problems to fix ──────────────────────────────────────────
    if (summary?.aiVisibility?.hasData && summary.aiVisibility.overallScore !== null) {
      const score = summary.aiVisibility.overallScore

      // Very low overall score
      if (score < 50) {
        actions.push({
          id: `low-score-${domain.id}`,
          domainId: domain.id,
          domainName: domain.domain,
          domainDisplayName: domain.displayName,
          tier: 1,
          title: 'Low SuperSchema Score',
          description: `${name} scored ${score}/100. Review recommendations to improve.`,
          ctaLabel: 'View Report',
          ctaRoute: `/domains/${slug}/ai-report`,
          icon: AlertTriangle,
        })
        continue
      }

      // Very weak category
      const cats = summary.aiVisibility.categoryScores
      if (cats) {
        const entries = Object.entries(cats) as [string, number][]
        const weakest = entries.sort(([, a], [, b]) => a - b)[0]
        if (weakest && weakest[1] < 30) {
          actions.push({
            id: `weak-cat-${domain.id}`,
            domainId: domain.id,
            domainName: domain.domain,
            domainDisplayName: domain.displayName,
            tier: 1,
            title: `Weak ${capitalize(weakest[0])} Score`,
            description: `${capitalize(weakest[0])} scored only ${weakest[1]}/100 on ${name}.`,
            ctaLabel: 'Fix Now',
            ctaRoute: `/domains/${slug}/ai-report`,
            icon: AlertTriangle,
          })
          continue
        }
      }
    }

    // ── TIER 2: Features to set up ───────────────────────────────────────
    if (!domain.hasScanData) {
      actions.push({
        id: `setup-scan-${domain.id}`,
        domainId: domain.id,
        domainName: domain.domain,
        domainDisplayName: domain.displayName,
        tier: 2,
        title: 'Run Your First Site Scan',
        description: `See how ready ${name} is for AI systems to understand.`,
        ctaLabel: 'Start Scan',
        ctaRoute: `/ai-analytics/site-score?domain=${slug}`,
        icon: Radar,
        isScanAction: true,
        isRescan: false,
      })
      continue
    }

    const businessProfileStatus = getBusinessProfileStatusForDomain(domain.domain, businessProfiles)

    // Missing Verified Business Profile → suggest canonical business facts before schema expansion.
    if (businessProfileStatus.state === 'missing') {
      actions.push({
        id: `setup-business-profile-${domain.id}`,
        domainId: domain.id,
        domainName: domain.domain,
        domainDisplayName: domain.displayName,
        tier: 2,
        title: canManageBusinessProfile ? 'Add Verified Business Profile' : 'Verified Business Profile Needed',
        description: canManageBusinessProfile
          ? 'Define the official business details SuperSchema uses to generate accurate, consistent schema for this domain.'
          : 'Ask a workspace owner/admin to complete this profile so generated schema can use official business details.',
        ctaLabel: canManageBusinessProfile ? 'Create profile' : 'View details',
        ctaRoute: `/domains/${slug}/settings#business-profile`,
        icon: ShieldCheck,
      })
      continue
    }

    if (!domain.hasBotTracking && isPro) {
      actions.push({
        id: `setup-bot-${domain.id}`,
        domainId: domain.id,
        domainName: domain.domain,
        domainDisplayName: domain.displayName,
        tier: 2,
        title: 'Enable Bot Tracking',
        description: `Track which AI bots visit ${name}.`,
        ctaLabel: 'Set Up',
        ctaRoute: `/domains/${slug}/bot-tracking`,
        icon: Bot,
      })
      continue
    }

    if (!domain.hasSchemaData) {
      actions.push({
        id: `setup-schema-${domain.id}`,
        domainId: domain.id,
        domainName: domain.domain,
        domainDisplayName: domain.displayName,
        tier: 2,
        title: 'Add Schema Markup',
        description: `Help AI engines understand ${name} with structured data.`,
        ctaLabel: 'Generate',
        ctaRoute: `/domains/${slug}/generate`,
        icon: Sparkles,
      })
      continue
    }

    // ── TIER 3: Opportunities to improve ─────────────────────────────────
    if (summary) {
      // Low schema coverage
      if (summary.schema?.hasData && summary.schema.coveragePercent < 50 && summary.schema.totalUrls > 0) {
        actions.push({
          id: `coverage-${domain.id}`,
          domainId: domain.id,
          domainName: domain.domain,
          domainDisplayName: domain.displayName,
          tier: 3,
          title: 'Improve Schema Coverage',
          description: `Only ${summary.schema.coveragePercent}% of pages on ${name} have schema.`,
          ctaLabel: 'View Pages',
          ctaRoute: `/domains/${slug}`,
          icon: TrendingUp,
        })
        continue
      }

      // Stale scan (>30 days)
      if (summary.aiVisibility?.scanDate) {
        const daysSince = Math.floor(
          (Date.now() - new Date(summary.aiVisibility.scanDate).getTime()) / 86400000
        )
        if (daysSince > 30) {
          actions.push({
            id: `rescan-${domain.id}`,
            domainId: domain.id,
            domainName: domain.domain,
            domainDisplayName: domain.displayName,
            tier: 3,
            title: 'Re-scan for Fresh Data',
            description: `Last scan was ${daysSince} days ago for ${name}.`,
            ctaLabel: 'Re-scan',
            ctaRoute: `/ai-analytics/site-score?domain=${slug}`,
            icon: Radar,
            isScanAction: true,
            isRescan: true,
          })
          continue
        }
      }

      // Weakest category below 60
      const cats = summary.aiVisibility?.categoryScores
      if (cats) {
        const entries = Object.entries(cats) as [string, number][]
        const weakest = entries.sort(([, a], [, b]) => a - b)[0]
        if (weakest && weakest[1] < 60) {
          actions.push({
            id: `optimize-${domain.id}`,
            domainId: domain.id,
            domainName: domain.domain,
            domainDisplayName: domain.displayName,
            tier: 3,
            title: `Optimize ${capitalize(weakest[0])}`,
            description: `${capitalize(weakest[0])} is your weakest area at ${weakest[1]}/100 on ${name}.`,
            ctaLabel: 'Improve',
            ctaRoute: `/domains/${slug}/ai-report`,
            icon: TrendingUp,
          })
          continue
        }
      }

      if (businessProfileStatus.state === 'linked_low_completeness') {
        actions.push({
          id: `improve-business-profile-${domain.id}`,
          domainId: domain.id,
          domainName: domain.domain,
          domainDisplayName: domain.displayName,
          tier: 3,
          title: 'Improve Verified Business Profile',
          description: `Your profile is ${businessProfileStatus.completenessScore}% complete. Add more canonical details for stronger schema consistency.`,
          ctaLabel: canManageBusinessProfile ? 'Improve profile' : 'View details',
          ctaRoute: `/domains/${slug}/settings#business-profile`,
          icon: ShieldCheck,
        })
        continue
      }
    }

    // ── TIER 4: Celebration ───────────────────────────────────────────────
    const score = summary?.aiVisibility?.overallScore ?? 0
    const coverage = summary?.schema?.coveragePercent ?? 0
    if (domain.hasScanData && domain.hasSchemaData && score >= 80 && coverage >= 80) {
      actions.push({
        id: `celebrate-${domain.id}`,
        domainId: domain.id,
        domainName: domain.domain,
        domainDisplayName: domain.displayName,
        tier: 4,
        title: 'Great Performance!',
        description: `${name} is scoring ${score}/100 with ${coverage}% schema coverage.`,
        ctaLabel: 'View Details',
        ctaRoute: `/domains/${slug}`,
        icon: CheckCircle2,
      })
    }
  }

  // Sort by tier (lowest = highest priority), take top 5
  return actions.sort((a, b) => a.tier - b.tier).slice(0, 5)
}


// ─── Component ───────────────────────────────────────────────────────────────

export function GuidedActions({
  domains,
  summaries,
  organizations,
  isPro,
  isLoading,
  canManageBusinessProfile = true,
  className,
}: GuidedActionsProps) {
  const navigate = useNavigate()
  const [isExpanded, setIsExpanded] = useState(false)
  const [scanModal, setScanModal] = useState<{
    domain: string
    displayName: string
    isRescan: boolean
  } | null>(null)

  const actions = useMemo(
    () => (isLoading ? [] : generateGuidedActions(domains, summaries, organizations, isPro, canManageBusinessProfile)),
    [domains, summaries, organizations, isPro, canManageBusinessProfile, isLoading]
  )

  // Don't render if no domains
  if (domains.length === 0) return null

  const shell = (children: React.ReactNode, head?: React.ReactNode) => (
    <DsIsland panel={false} ground={false} className={className}>
      <div data-testid="guided-actions-panel">
        {/*
          "Recommended next steps" under a monospace "PRIORITY QUEUE" eyebrow.
          Two names for one list, and one of them was jargon.
        */}
        <Card title="What to do next" head={head}>
          {children}
        </Card>
      </div>
    </DsIsland>
  )

  if (isLoading) {
    return shell(
      <div className="ds-dash__todowait">
        {[1, 2, 3].map((i) => (
          <div key={i}>
            <Skeleton width="45%" height={15} />
            <div style={{ marginTop: 8 }}>
              <Skeleton width="80%" height={12} />
            </div>
          </div>
        ))}
      </div>
    )
  }

  /*
    Nothing to do used to be a party popper springing in on a green tile
    saying "All caught up! Your domains are looking great. Keep up the
    excellent work." Somebody who has nothing to do wants to know that we
    looked, and when.
  */
  if (actions.length === 0 || actions.every((a) => a.tier === 4)) {
    return shell(
      <Proof
        title="Nothing needs you right now"
        detail="We read every site you have and found nothing worth interrupting you for."
      />
    )
  }

  const activeActions = actions.filter((a) => a.tier !== 4)
  const visibleActions = isExpanded ? activeActions : activeActions.slice(0, 3)

  return shell(
    <>
      <ToDo
        items={visibleActions.map((action) => ({
          title: action.title,
          detail: action.description,
          /*
            The site this is about. It used to be set in uppercase monospace
            under the description, which made an address look like a serial
            number; it is the plainest fact in the row.
          */
          cost: `${tierLabel[action.tier]} · ${action.domainName}`,
          action: (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (action.isScanAction) {
                  setScanModal({
                    domain: action.domainName,
                    displayName: action.domainDisplayName || action.domainName,
                    isRescan: !!action.isRescan,
                  })
                } else {
                  navigate(action.ctaRoute)
                }
              }}
            >
              {action.ctaLabel}
            </Button>
          ),
        }))}
      />

      <ScanTypeModal
        isOpen={!!scanModal}
        onClose={() => setScanModal(null)}
        domain={scanModal?.domain || ''}
        displayName={scanModal?.displayName || ''}
        isPro={isPro}
        isRescan={scanModal?.isRescan || false}
      />
    </>,
    activeActions.length > 3 ? (
      <Button variant="ghost" size="sm" onClick={() => setIsExpanded((value) => !value)}>
        {isExpanded ? 'Show the top three' : `Show all ${activeActions.length}`}
      </Button>
    ) : undefined
  )
}
